import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import type { MediaItem } from "@/types/media";

interface HeroCarouselProps {
  items: MediaItem[];
}

export default function HeroCarousel({ items }: HeroCarouselProps) {
  const [active, setActive] = useState(0);
  const touchStart = useRef<number | null>(null);

  useEffect(() => {
    if (items.length <= 1) return undefined;
    const timer = window.setInterval(() => {
      setActive((prev) => (prev + 1) % items.length);
    }, 6000);
    return () => window.clearInterval(timer);
  }, [items.length]);

  if (items.length === 0) return null;

  const current = items[active];

  const go = (dir: number) => {
    setActive((prev) => (prev + dir + items.length) % items.length);
  };

  return (
    <section
      className="relative h-[360px] w-full overflow-hidden sm:h-[440px] md:h-[560px]"
      onTouchStart={(e) => {
        touchStart.current = e.touches[0].clientX;
      }}
      onTouchEnd={(e) => {
        if (touchStart.current === null) return;
        const diff = e.changedTouches[0].clientX - touchStart.current;
        if (Math.abs(diff) > 50) go(diff > 0 ? -1 : 1);
        touchStart.current = null;
      }}
    >
      {items.map((item, idx) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            idx === active ? "opacity-100" : "opacity-0"
          }`}
          aria-hidden={idx !== active}
        >
          <img
            src={item.backdrop}
            alt={`${item.title} 焦点推荐主图`}
            title={`${item.title} 西瓜影院高清推荐`}
            className="h-full w-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background-50 via-background-50/70 to-background-50/20" />
          <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-transparent to-transparent" />
        </div>
      ))}

      <div className="relative z-10 mx-auto flex h-full items-center px-4 md:px-8">
        <div key={current.id} className="animate-reveal max-w-xl">
          <span className="inline-flex items-center gap-1 rounded-full bg-primary-500 px-3 py-1 text-xs font-700 text-background-950">
            <i className="ri-fire-fill text-sm" aria-hidden="true"></i>
            焦点推荐
          </span>
          <h2 className="mt-4 font-heading text-3xl font-700 leading-tight text-foreground-950 md:text-5xl">
            {current.title}
          </h2>
          <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-foreground-300">
            <span className="flex items-center gap-1 font-700 text-accent-400">
              <i className="ri-star-fill text-sm" aria-hidden="true"></i>
              {current.rating.toFixed(1)}
            </span>
            <span>{current.year}</span>
            <span className="hidden sm:inline">·</span>
            <span className="hidden sm:inline">{current.region}</span>
            <span className="hidden sm:inline">·</span>
            <span className="hidden sm:inline">{current.genres.join(" / ")}</span>
          </div>
          <p className="mt-4 line-clamp-3 max-w-lg text-sm leading-6 text-foreground-400">{current.synopsis}</p>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Link
              to={`/detail/${current.id}`}
              className="inline-flex h-11 items-center gap-2 rounded-md bg-primary-500 px-6 text-sm font-500 text-background-950 transition-colors hover:bg-primary-600"
            >
              <i className="ri-play-circle-line text-lg" aria-hidden="true"></i>
              立即查看
            </Link>
            <Link
              to={current.type === "movie" ? "/category/movie" : "/category/tv"}
              className="inline-flex h-11 items-center gap-2 rounded-md border border-background-300 px-6 text-sm font-500 text-foreground-300 transition-colors hover:border-primary-500 hover:text-primary-400"
            >
              <i className="ri-list-check text-base" aria-hidden="true"></i>
              更多同类
            </Link>
          </div>
        </div>
      </div>

      <div className="absolute bottom-5 left-4 z-10 flex items-center gap-2 md:left-8">
        {items.map((item, idx) => (
          <button
            key={item.id}
            type="button"
            aria-label={`切换到第 ${idx + 1} 张推荐`}
            onClick={() => setActive(idx)}
            className={`h-2 rounded-full transition-all duration-300 ${
              idx === active ? "w-8 bg-primary-500" : "w-2 bg-foreground-600 hover:bg-foreground-700"
            }`}
          />
        ))}
      </div>

      <div className="absolute bottom-5 right-4 z-10 hidden items-center gap-2 md:right-8 md:flex">
        <button
          type="button"
          onClick={() => go(-1)}
          aria-label="上一张推荐"
          className="flex h-10 w-10 items-center justify-center rounded-full border border-background-300 bg-background-50/70 text-foreground-950 backdrop-blur transition-colors hover:border-primary-500 hover:text-primary-400"
        >
          <i className="ri-arrow-left-s-line text-xl" aria-hidden="true"></i>
        </button>
        <button
          type="button"
          onClick={() => go(1)}
          aria-label="下一张推荐"
          className="flex h-10 w-10 items-center justify-center rounded-full border border-background-300 bg-background-50/70 text-foreground-950 backdrop-blur transition-colors hover:border-primary-500 hover:text-primary-400"
        >
          <i className="ri-arrow-right-s-line text-xl" aria-hidden="true"></i>
        </button>
      </div>
    </section>
  );
}